import { Button, ErrorState, FlagKeyPill, LoadingRows } from '@shared/ui';
import { Link } from 'react-router';

import { useFailedItems } from '../hooks/use-release-operation';

import { resultRows } from './operation-results';

/**
 * Failed items of a partial operation, with their reasons (ACR-1601), 50 per
 * page. Flags and clients link to their own screens. The unrecorded target chunks
 * are one row that names no client.
 */
export function OperationResults({
    releaseId,
    operationId,
}: {
    releaseId: string;
    operationId: string;
}) {
    const items = useFailedItems(releaseId, operationId, true);

    if (items.isPending) return <LoadingRows label="Loading failed items…" />;
    if (items.isError) {
        return <ErrorState error={items.error} onRetry={() => void items.refetch()} />;
    }

    const rows = resultRows(items.data.pages);
    if (rows.length === 0) return null;

    return (
        <div className="flex flex-col gap-2">
            <h5 className="text-[11px] font-semibold uppercase tracking-wide text-content-muted">
                Failed items
            </h5>
            <ul className="flex flex-col gap-1.5 text-[13px]">
                {rows.map((row) =>
                    row.kind === 'targets-incomplete' ? (
                        <li key={row.id} className="text-content-body">
                            {row.label}
                        </li>
                    ) : (
                        <li key={row.id} className="flex flex-wrap items-center gap-2">
                            <Link to={row.flagHref}>
                                <FlagKeyPill>{row.flagKey}</FlagKeyPill>
                            </Link>
                            {row.clientHref !== null && row.tenantId !== null && (
                                <Link
                                    to={row.clientHref}
                                    className="text-accent-text hover:underline"
                                >
                                    {row.tenantId}
                                </Link>
                            )}
                            <span className="text-content-strong">{row.resultLabel}</span>
                            {row.reason !== undefined && (
                                <span className="text-xs text-content-muted">{row.reason}</span>
                            )}
                            {row.note !== undefined && (
                                <span className="text-xs text-content-muted">{row.note}</span>
                            )}
                        </li>
                    ),
                )}
            </ul>
            {items.hasNextPage && (
                <div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void items.fetchNextPage()}
                        disabled={items.isFetchingNextPage}
                    >
                        {items.isFetchingNextPage ? 'Loading…' : 'Show more'}
                    </Button>
                </div>
            )}
        </div>
    );
}
